import React, { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { useLanguage } from '../../../contexts/LanguageContext';

/**
 * Tutar string'ini sayıya çevirir
 * "200000,00" veya "200000.00" formatını destekler
 */
const parseAmount = (amountStr) => {
    if (!amountStr) return null;
    // Virgülü noktaya çevir (Türkçe format için)
    const normalized = amountStr.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
};

const PaymentFailure = () => {
    const { th } = useLanguage();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    // URL'den hata bilgilerini al
    const errorInfo = useMemo(() => ({
        transactionId: searchParams.get('transactionId'),
        errorCode: searchParams.get('errorCode'),
        errorMessage: searchParams.get('errorMessage'),
        referenceCode: searchParams.get('referenceCode'),
        // Yeni alanlar
        paymentType: searchParams.get('paymentType'),
        bankName: searchParams.get('bankName'),
        amount: parseAmount(searchParams.get('amount')),
        currency: searchParams.get('currency') || 'TRY',
        installmentCount: searchParams.get('installmentCount')
    }), [searchParams]);

    // Ödeme tipi formatı
    const formattedPaymentType = useMemo(() => {
        if (errorInfo.paymentType) {
            return errorInfo.paymentType;
        }
        if (errorInfo.installmentCount) {
            const count = parseInt(errorInfo.installmentCount, 10);
            if (count > 1) {
                return `${count} ${th('creditCardCollection.labels.installment') || 'Taksit'}`;
            }
        }
        return th('creditCardCollection.labels.singlePayment') || 'Tek Çekim';
    }, [errorInfo.paymentType, errorInfo.installmentCount, th]);

    // Para birimi sembolü
    const currencySymbol = errorInfo.currency === 'TRY' ? '₺' : errorInfo.currency;

    const handleRetry = () => {
        navigate('/parts/credit-card-collection');
    };

    const handleGoHome = () => {
        navigate('/');
    };

    return (
        <div className="flex align-items-start justify-content-center min-h-screen surface-ground pt-6">
            <Card className="w-full md:w-6 lg:w-4 shadow-4">
                <div className="text-center">
                    {/* Hata İkonu */}
                    <div className="mb-4">
                        <i 
                            className="pi pi-times-circle text-red-500" 
                            style={{ fontSize: '5rem' }}
                        />
                    </div>

                    {/* Başlık */}
                    <h1 className="text-3xl font-bold text-red-600 mb-3">
                        {th('creditCardCollection.failure.title')}
                    </h1>

                    {/* Alt Başlık */}
                    <p className="text-xl text-600 mb-5">
                        {th('creditCardCollection.failure.subtitle')}
                    </p>

                    {/* Hata Bilgileri */}
                    <div className="surface-100 border-round-xl p-4 mb-5 text-left">
                        {errorInfo.errorMessage && (
                            <div className="flex flex-column py-2 border-bottom-1 surface-border">
                                <span className="text-600 mb-1">
                                    {th('creditCardCollection.labels.errorMessage') || 'Hata Mesajı'}
                                </span>
                                <span className="font-semibold text-red-600">
                                    {decodeURIComponent(errorInfo.errorMessage)}
                                </span>
                            </div>
                        )}

                        {errorInfo.errorCode && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.errorCode') || 'Hata Kodu'}
                                </span>
                                <span className="font-semibold text-900">
                                    {errorInfo.errorCode}
                                </span>
                            </div>
                        )}

                        {errorInfo.transactionId && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.transactionId') || 'İşlem No'}
                                </span>
                                <span className="font-semibold text-900">
                                    {errorInfo.transactionId}
                                </span>
                            </div>
                        )}

                        {errorInfo.referenceCode && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.referenceCode') || 'Referans Kodu'}
                                </span>
                                <span className="font-semibold text-900">
                                    {errorInfo.referenceCode}
                                </span>
                            </div>
                        )}

                        {errorInfo.bankName && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.bankName') || 'Banka'}
                                </span>
                                <span className="font-semibold text-900">
                                    {errorInfo.bankName}
                                </span>
                            </div>
                        )}

                        {(errorInfo.paymentType || errorInfo.installmentCount) && (
                            <div className="flex justify-content-between align-items-center py-2 border-bottom-1 surface-border">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.paymentType') || 'Ödeme Tipi'}
                                </span>
                                <span className="font-semibold text-900">
                                    {formattedPaymentType}
                                </span>
                            </div>
                        )}

                        {errorInfo.amount && (
                            <div className="flex justify-content-between align-items-center py-2">
                                <span className="text-600">
                                    {th('creditCardCollection.labels.amount') || 'Tutar'}
                                </span>
                                <span className="font-bold text-xl text-900">
                                    {new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(errorInfo.amount)} {currencySymbol}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Bilgi Mesajı */}
                    <div className="bg-orange-50 border-round-xl p-3 mb-5">
                        <i className="pi pi-exclamation-triangle text-orange-500 mr-2" />
                        <span className="text-orange-700">
                            {th('creditCardCollection.failure.info')}
                        </span>
                    </div>

                    {/* Olası Nedenler */}
                    <div className="text-left mb-5">
                        <h4 className="text-600 mb-3">
                            {th('creditCardCollection.failure.possibleReasons')}
                        </h4>
                        <ul className="text-600 pl-4 m-0 line-height-3">
                            <li>{th('creditCardCollection.failure.reason1')}</li>
                            <li>{th('creditCardCollection.failure.reason2')}</li>
                            <li>{th('creditCardCollection.failure.reason3')}</li>
                            <li>{th('creditCardCollection.failure.reason4')}</li>
                            <li>{th('creditCardCollection.failure.reason5')}</li>
                        </ul>
                    </div>

                    {/* Butonlar */}
                    <div className="flex flex-column md:flex-row gap-3 justify-content-center">
                        <Button
                            label={th('creditCardCollection.buttons.retry')}
                            icon="pi pi-refresh"
                            className="p-button-warning"
                            onClick={handleRetry}
                        />
                        <Button
                            label={th('creditCardCollection.buttons.goHome')}
                            icon="pi pi-home"
                            className="p-button-outlined"
                            onClick={handleGoHome}
                        />
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default PaymentFailure;

